import type { ReactNode } from "react";

/** Deep Navy is the figure color for money; Ember marks Home's With M3P tile and Forest the low end of a range. */
type CostTone = "navy" | "ember" | "forest";

const AMOUNT_COLOR: Record<CostTone, string> = {
  navy: "text-deep-navy",
  ember: "text-ember",
  forest: "text-forest",
};

interface CostFigureProps {
  /** The formatted amount, or null while the estimate is unavailable. */
  amount: string | null;
  /** Rendered after the amount, such as "/month"; absent for a bare amount. */
  unit?: string | undefined;
  /** Colors the amount; only Ember colors the unit too. "Still calculating" ignores it. */
  tone?: CostTone;
}

/**
 * A money figure with its unit, or "Still calculating" without a unit.
 *
 * Classes compose as plain strings, not with cn(): tailwind-merge can't tell
 * the custom text-size tokens (text-num-lg, text-body-sm) from the text-*
 * colors and would drop the size token.
 */
export function CostFigure({ amount, unit, tone = "navy" }: CostFigureProps): ReactNode {
  if (amount === null) {
    return <p className="text-num-lg text-deep-navy font-serif">Still calculating</p>;
  }
  return (
    <p className={`text-num-lg font-serif ${AMOUNT_COLOR[tone]}`}>
      {amount}
      <span
        className={`text-body-sm font-sans ${tone === "ember" ? "text-ember" : "text-slate-teal"}`}
      >
        {unit}
      </span>
    </p>
  );
}
